import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { prependNotification } from '../features/notificationsSlice';
import { getNewToken } from '../services/authApi';
import { isTokenExpired } from '../utils/jwt';
import { isPlaybackActive } from '../utils/playbackPresence';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://api.doramahype.ru/api/v1/ws';

// Интервал прикладного heartbeat'а. Именно он, а не открытый сокет, означает
// «пользователь онлайн»: на protocol-level ping отвечает сам браузер, без
// участия страницы, поэтому фоновая вкладка держала бы соединение живым
// бесконечно и вечно числилась онлайн.
const HEARTBEAT_MS = 30_000;

// Потолок паузы между попытками переподключения.
const MAX_RECONNECT_MS = 30_000;

const GUEST_ID_KEY = 'guest_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Фолбэк: randomUUID есть только в защищённом контексте (https/localhost).
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // версия 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // вариант
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Идентификатор гостя живёт в localStorage: он переживает переподключения,
// вкладки и перезапуск браузера. Кука для этого ненадёжна — Set-Cookie в ответе
// WebSocket-рукопожатия (101) обрабатывается браузерами непоследовательно.
function getGuestId() {
  try {
    let id = localStorage.getItem(GUEST_ID_KEY);
    if (!id || !UUID_RE.test(id)) {
      id = randomUuid();
      localStorage.setItem(GUEST_ID_KEY, id);
    }
    return id;
  } catch {
    return null; // приватный режим или заблокированный storage — бэкенд выдаст свой UUID
  }
}

function clearGuestId() {
  try {
    localStorage.removeItem(GUEST_ID_KEY);
  } catch {
    // no-op
  }
}

export function useOnlineSocket() {
  // Следим за самим токеном — если он обновился (после refresh), пересоздаём соединение
  const accessToken = useSelector((state) => state.auth.accessToken);
  const dispatch = useDispatch();
  const aliveRef = useRef(true);
  const timerRef = useRef(null);
  const beatRef = useRef(null);
  const attemptRef = useRef(0);
  const wsRef = useRef(null);
  const refreshingRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;

    // Авторизованный пользователь гостем больше не считается.
    if (accessToken) clearGuestId();

    function stopBeat() {
      clearInterval(beatRef.current);
      beatRef.current = null;
    }

    function sendBeat() {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      // Видимость вкладки — плохая мера присутствия для сайта с видео: человек
      // с играющей серией присутствует, даже если ушёл в соседнюю вкладку или
      // свернул браузер. Поэтому воспроизведение засчитывается наравне.
      if (document.visibilityState === 'visible' || isPlaybackActive()) {
        ws.send('{"type":"ping"}');
      }
    }

    function connect() {
      // Пока сети нет, попытки бессмысленны — разбудит слушатель 'online'.
      if (!aliveRef.current || navigator.onLine === false) return;

      // Протухший токен бэкенд отвергает с 401, а WebSocket API по спецификации
      // не отдаёт статус проваленного рукопожатия — отличить «токен умер» от
      // «сеть отвалилась» в onclose невозможно. Поэтому проверяем срок сами:
      // иначе вкладка, простоявшая ночь без единого REST-запроса (только они
      // тянут refresh через RTK Query), долбилась бы мёртвым токеном вечно.
      if (accessToken && isTokenExpired(accessToken)) {
        if (!refreshingRef.current) {
          refreshingRef.current = true;
          getNewToken()
            .catch(() => {
              // refresh не удался — getNewToken сам вызовет logout,
              // accessToken станет null и эффект переподключит нас гостем
            })
            .finally(() => {
              refreshingRef.current = false;
            });
        }
        // Успешный refresh поменяет accessToken в Redux и перезапустит эффект.
        // Подстраховка на случай, если этого не произойдёт:
        timerRef.current = setTimeout(connect, 30_000);
        return;
      }

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      stopBeat();

      let url = WS_URL;
      if (accessToken) {
        url = `${WS_URL}?token=${encodeURIComponent(accessToken)}`;
      } else {
        const sid = getGuestId();
        if (sid) url = `${WS_URL}?sid=${encodeURIComponent(sid)}`;
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        sendBeat();
        beatRef.current = setInterval(sendBeat, HEARTBEAT_MS);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification') {
            dispatch(prependNotification(msg.payload));
          }
        } catch {
          // игнорируем не-JSON сообщения (например ping-фреймы)
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        stopBeat();
        if (!aliveRef.current) return;
        const backoff = Math.min(1000 * 2 ** attemptRef.current, MAX_RECONNECT_MS);
        attemptRef.current += 1;
        // Jitter обязателен: без него после рестарта бэкенда все клиенты
        // ломятся переподключаться одновременно.
        timerRef.current = setTimeout(connect, backoff * (0.7 + Math.random() * 0.6));
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    function reconnectNow() {
      if (!aliveRef.current) return;
      clearTimeout(timerRef.current);
      attemptRef.current = 0;
      const ws = wsRef.current;
      // CONNECTING (0) и OPEN (1) трогать не нужно, CLOSING/CLOSED — нужно.
      if (!ws || ws.readyState > WebSocket.OPEN) connect();
    }

    function onVisibility() {
      if (document.visibilityState !== 'visible') return;
      // Вернулись на вкладку — засчитываемся сразу, а не через HEARTBEAT_MS.
      reconnectNow();
      sendBeat();
    }

    connect();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', reconnectNow);

    return () => {
      aliveRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', reconnectNow);
      clearTimeout(timerRef.current);
      stopBeat();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  // Пересоздаём соединение при смене токена (логин, логаут, refresh)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);
}
